import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The bits of the Web Speech API this app uses, declared here because
 * TypeScript's own DOM library still does not carry SpeechRecognition (it has
 * the result types but not the recogniser itself), and because a narrow local
 * declaration is honest about which browsers this is aimed at.
 */
interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: { error?: string }) => void) | null;
  onend: (() => void) | null;
}

interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: {
    length: number;
    [index: number]: { isFinal: boolean; 0: { transcript: string } };
  };
}

type RecognitionConstructor = new () => SpeechRecognitionLike;

const recognitionConstructor = (): RecognitionConstructor | undefined => {
  const globals = window as unknown as {
    SpeechRecognition?: RecognitionConstructor;
    webkitSpeechRecognition?: RecognitionConstructor;
  };
  return globals.SpeechRecognition ?? globals.webkitSpeechRecognition;
};

/** i18next gives us "es"; the recogniser wants a real locale or it falls back to the browser's. */
const RECOGNITION_LOCALES: Record<string, string> = {
  es: 'es-ES',
  en: 'en-GB',
  sk: 'sk-SK',
};

export const speechLocaleFor = (language: string): string =>
  RECOGNITION_LOCALES[language.split('-')[0]] ?? language;

/**
 * How often a guess is allowed to reach React while somebody is mid-sentence.
 *
 * A recogniser re-sends its working-out several times a second, and each one
 * used to set state on a page carrying a form, a photo grid and an assistant
 * block. On a desktop that is invisible; on a phone it is the render loop
 * eating the main thread, which is exactly what a frozen page is. Settled
 * words never wait -- only the guesses do.
 */
const INTERIM_FLUSH_MS = 180;

export interface SpeechDictation {
  /**
   * False on a browser without the API (Firefox, and anything old). Callers
   * hide the microphone entirely rather than offering a button that does
   * nothing -- typing has to keep working, because for some people it is the
   * only thing that will.
   */
  supported: boolean;
  listening: boolean;
  /** What has been heard so far, settled and still-being-worked-out together. */
  transcript: string;
  /** Set when the recogniser itself failed, most often 'not-allowed' -- the microphone was refused. */
  error?: string;
  /**
   * `continueFrom` is whatever is already in the field being dictated into.
   * Without it, somebody who types half a sentence and then reaches for the
   * microphone watches their own words disappear.
   */
  start: (continueFrom?: string) => void;
  stop: () => void;
  reset: () => void;
}

/**
 * Speech to text, in the browser, for nothing.
 *
 * This is the platform's own recogniser: no audio leaves the page through us,
 * there is no per-minute cost, and it works with the phone in a jacket pocket
 * and a hand on a shovel -- which is the actual situation this exists for. A
 * provider standing in a trench is not going to type a quote.
 *
 * Deliberately dictation and nothing more: it hands back words. What those
 * words mean is the caller's problem, which is what lets the same hook serve
 * the job description, a spoken change to a quote, and whatever wants it next.
 *
 * **Phones are where this gets hard**, and the three things that bit:
 *
 * - A recogniser that dies without firing onend used to leave a stale
 *   reference behind, and every later tap of the microphone then did nothing
 *   at all. Stopping now clears that reference itself rather than trusting an
 *   event that may never arrive, and starting throws away whatever it finds.
 * - Handlers from a session that has ended cannot write over a newer one. Each
 *   session carries a number and stragglers are ignored.
 * - The transcript is rebuilt from the whole result list each time instead of
 *   appended to. Appending depends on resultIndex being exactly what you
 *   expect, and when it is not, the same words land twice.
 */
export const useSpeechDictation = (language: string): SpeechDictation => {
  const [supported] = useState(() => recognitionConstructor() !== undefined);
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState<string>();

  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  // Bumped for every session. A handler belonging to an older one checks this
  // and says nothing -- which is what keeps the tail of a stopped sentence
  // from overwriting a new one.
  const sessionRef = useRef(0);
  // What earlier sessions settled on. Stopping and starting again continues
  // the sentence instead of wiping it.
  const baseRef = useRef('');
  const flushRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<string | null>(null);

  const cancelFlush = () => {
    if (flushRef.current === null) return;
    clearTimeout(flushRef.current);
    flushRef.current = null;
  };

  const publish = useCallback((text: string, immediately: boolean) => {
    pendingRef.current = text;
    if (immediately) {
      cancelFlush();
      setTranscript(text);
      return;
    }
    if (flushRef.current !== null) return;
    flushRef.current = setTimeout(() => {
      flushRef.current = null;
      if (pendingRef.current !== null) setTranscript(pendingRef.current);
    }, INTERIM_FLUSH_MS);
  }, []);

  const stop = useCallback(() => {
    const recognition = recognitionRef.current;
    // Cleared here, and not left to onend. On a phone that event sometimes
    // never comes, and a reference nobody cleared is a microphone button that
    // never works again.
    recognitionRef.current = null;
    setListening(false);

    // Whatever was on screen is what the sentence is now, so a later session
    // continues from it rather than replacing it.
    if (pendingRef.current !== null) {
      cancelFlush();
      baseRef.current = pendingRef.current;
      setTranscript(pendingRef.current);
    }

    if (!recognition) return;
    // onresult stays attached on purpose: Chrome delivers one last settled
    // result after stop(), and those are the words somebody just said. Only
    // onend goes, because the reference it would clear is already gone.
    recognition.onend = null;
    try {
      recognition.stop();
    } catch {
      // Already finished. Nothing to stop and nothing to report.
    }
  }, []);

  const reset = useCallback(() => {
    cancelFlush();
    baseRef.current = '';
    pendingRef.current = null;
    setTranscript('');
    setError(undefined);
  }, []);

  const start = useCallback((continueFrom?: string) => {
    const Recognition = recognitionConstructor();
    if (!Recognition) return;

    // Anything still around belongs to a session that is over -- most often
    // one that died quietly on a phone. Throw it away and carry on rather
    // than returning, which is what used to make the microphone dead.
    const previous = recognitionRef.current;
    if (previous) {
      recognitionRef.current = null;
      previous.onend = null;
      try {
        previous.abort();
      } catch {
        // Already gone.
      }
    }

    const session = sessionRef.current + 1;
    sessionRef.current = session;
    // What the field already holds wins over what this hook remembers: the
    // person may have typed, or edited what was dictated a moment ago, and
    // either way the screen is the truth.
    baseRef.current = continueFrom ?? pendingRef.current ?? baseRef.current;
    pendingRef.current = baseRef.current;

    const recognition = new Recognition();
    recognition.lang = speechLocaleFor(language);
    // Someone describing a job stops to think. Without continuous mode the
    // recogniser closes on the first pause and they lose the second half of
    // the sentence.
    recognition.continuous = true;
    recognition.interimResults = true;

    recognition.onresult = (event) => {
      if (sessionRef.current !== session) return;

      // Rebuilt from the whole list, never appended to: a recogniser re-sends
      // the tail of what it is still working out, and appending from
      // resultIndex lands the same words twice the moment that index is not
      // what you assumed.
      let heard = '';
      for (let i = 0; i < event.results.length; i += 1) {
        const result = event.results[i];
        if (result) heard += `${result[0].transcript} `;
      }

      // Settled words go straight through; guesses wait their turn.
      let settled = false;
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        if (event.results[i]?.isFinal) {
          settled = true;
          break;
        }
      }

      publish(`${baseRef.current} ${heard}`.replace(/\s+/g, ' ').trim(), settled);
    };

    recognition.onerror = (event) => {
      if (sessionRef.current !== session) return;
      // 'no-speech' and 'aborted' are what happens when somebody taps the
      // microphone and changes their mind. Neither is worth a red message.
      if (event.error && event.error !== 'no-speech' && event.error !== 'aborted') setError(event.error);
      recognitionRef.current = null;
      setListening(false);
    };

    recognition.onend = () => {
      if (sessionRef.current !== session) return;
      recognitionRef.current = null;
      setListening(false);
      // The recogniser closed on its own -- a pause it decided was the end, or
      // the phone taking the microphone back. Keep what it heard.
      if (pendingRef.current !== null) baseRef.current = pendingRef.current;
    };

    recognitionRef.current = recognition;
    setError(undefined);
    setListening(true);

    try {
      recognition.start();
    } catch {
      // Refused by the browser before it ever ran (no permission yet, or an
      // insecure origin). The button goes back to idle rather than pretending
      // to listen.
      recognitionRef.current = null;
      setListening(false);
    }
  }, [language, publish]);

  // Leaving the screen mid-sentence must not leave the microphone open.
  useEffect(
    () => () => {
      cancelFlush();
      sessionRef.current += 1;
      recognitionRef.current?.abort();
      recognitionRef.current = null;
    },
    []
  );

  return { supported, listening, transcript, error, start, stop, reset };
};
